'use client';

import { useCallback } from 'react';
import { usePathname, useParams } from 'next/navigation';

interface ErrorContext {
  component?: string;
  action?: string;
  additionalData?: Record<string, unknown>;
}

interface ReportErrorOptions {
  error: Error | string;
  context?: ErrorContext;
  silent?: boolean;
}

/**
 * Hook for reporting errors to the app_status system
 * Use this to manually report errors from components or API calls
 */
export function useErrorReporting() {
  const pathname = usePathname();
  const params = useParams();

  const getEntityInfo = useCallback(() => {
    let entity = 'PAGE';
    let entityId: string | null = null;

    if (pathname?.includes('/scenarios/')) {
      entity = 'SCENARIO';
      entityId = (params?.scenarioId as string) || (params?.id as string) || null;
    } else if (pathname?.includes('/plans/')) {
      entity = 'PLAN';
      entityId = (params?.planId as string) || (params?.id as string) || null;
    } else if (pathname?.includes('/walkforward')) {
      entity = 'WALKFORWARD';
      entityId = (params?.id as string) || null;
    } else if (pathname?.includes('/administration/listprice')) {
      entity = 'LISTPRICE';
    } else if (pathname?.includes('/administration/volume')) {
      entity = 'VOLUME';
    } else if (pathname?.includes('/export')) {
      entity = 'EXPORT';
    }

    return { entity, entityId };
  }, [pathname, params]);

  const reportError = useCallback(async ({ error, context, silent = false }: ReportErrorOptions) => {
    try {
      const { entity, entityId } = getEntityInfo();
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorStack = error instanceof Error ? error.stack : undefined;

      const response = await fetch('/api/app-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          entity,
          entity_id: entityId,
          reported_by: 'LAMBDA', // Using LAMBDA for frontend auto-reported errors
          reporter_name: 'Frontend Error Boundary',
          operation: 'VIEW',
          status: 'ERROR',
          message: errorMessage.substring(0, 1000), // Truncate to 1000 chars
          metadata: {
            page_url: pathname,
            user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
            timestamp: new Date().toISOString(),
            component: context?.component,
            action: context?.action,
            stack_trace: errorStack?.substring(0, 2000), // Truncate stack trace
            ...context?.additionalData,
          },
        }),
      });

      if (!response.ok && !silent) {
        console.error('Failed to report error to app_status');
      }

      return response.ok;
    } catch (reportingError) {
      // Don't throw if error reporting itself fails
      if (!silent) {
        console.error('Error reporting failed:', reportingError);
      }
      return false;
    }
  }, [getEntityInfo, pathname]);

  return { reportError, getEntityInfo };
}

/**
 * Standalone function to report errors without hook context
 * Useful for error boundaries and global error handlers
 */
export async function reportErrorToAppStatus(
  error: Error | string,
  metadata: {
    page_url?: string;
    component?: string;
    action?: string;
    entity?: string;
    entity_id?: string;
    [key: string]: unknown;
  } = {}
) {
  try {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorStack = error instanceof Error ? error.stack : undefined;

    const response = await fetch('/api/app-status', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        entity: metadata.entity || 'PAGE',
        entity_id: metadata.entity_id || null,
        reported_by: 'LAMBDA',
        reporter_name: 'Frontend Error Handler',
        operation: 'VIEW',
        status: 'ERROR',
        message: errorMessage.substring(0, 1000),
        metadata: {
          page_url: metadata.page_url || (typeof window !== 'undefined' ? window.location.pathname : 'unknown'),
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
          timestamp: new Date().toISOString(),
          stack_trace: errorStack?.substring(0, 2000),
          ...metadata,
        },
      }),
    });

    return response.ok;
  } catch {
    console.error('Error reporting failed');
    return false;
  }
}
